import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MapPin, ShieldCheck, Clock } from 'lucide-react';

/**
 * BackgroundLocationDisclosure
 *
 * Google Play / Apple compliant PROMINENT DISCLOSURE shown BEFORE the OS
 * background-location permission prompt. Required for apps requesting
 * ACCESS_BACKGROUND_LOCATION. Must state, in plain language, that the app
 * collects location in the background ("even when the app is closed or not in
 * use") and obtain the user's affirmative consent before requesting permission.
 */
const BackgroundLocationDisclosure = ({ isOpen, onAllow, onDecline }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onDecline(); }}>
      <DialogContent className="max-w-md" data-testid="bg-location-disclosure">
        <DialogHeader>
          <div className="mx-auto w-14 h-14 bg-cyan-100 rounded-full flex items-center justify-center mb-2">
            <MapPin className="w-7 h-7 text-cyan-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            Allow Boat 360 to use your location in the background?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-gray-700">
            <strong>Boat 360 collects location data to enable trip tracking and crew
            safety alerts even when the app is closed or not in use.</strong>
          </p>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Users3Icon />
              <p className="text-sm text-gray-600">
                Your crew can see your position on the water during an active trip.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">
                Overdue and SOS alerts can be sent automatically if you don&apos;t return on time.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">
                Background location is <strong>only used during an active trip</strong> and
                stops automatically as soon as you end your trip.
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            On the next screen, choose &quot;Allow all the time&quot; to enable background tracking.
            You can turn this off anytime in Settings.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={onAllow}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
            data-testid="bg-location-allow-btn"
          >
            Allow
          </Button>
          <Button
            variant="ghost"
            onClick={onDecline}
            className="w-full text-gray-500"
            data-testid="bg-location-decline-btn"
          >
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Small inline icon to avoid an extra import name clash
const Users3Icon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
    fill="none" stroke="#0891b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className="shrink-0 mt-0.5">
    <path d="M18 21a8 8 0 0 0-16 0" /><circle cx="10" cy="8" r="5" />
    <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
  </svg>
);

export default BackgroundLocationDisclosure;
